import { IMenuQrLayoutProfile, IMenuQrRect } from './menu-qr-layout';

export type TMenuQrPosterSize = 'square' | 'vertical';

/**
 * A poster is the artwork PNG (13x13 cm or 10x15 cm at 300 DPI) plus an
 * overlay. `window` is the white area inside the orange band where the QR
 * must sit; `bottomTextMask` covers the artwork's bottom text (inside the
 * light grey frame, over pure white) for the orders design.
 */
export interface IMenuQrPosterSpec {
  widthPx: number;
  heightPx: number;
  widthMm: number;
  heightMm: number;
  artworkFile: 'qrbg.png' | 'qrbg1015.png';
  window: IMenuQrRect;
  bottomTextMask: IMenuQrRect;
  ordersProfile: IMenuQrLayoutProfile;
}

export const MENU_QR_POSTERS: Record<TMenuQrPosterSize, IMenuQrPosterSpec> = {
  square: {
    widthPx: 1535,
    heightPx: 1535,
    widthMm: 130,
    heightMm: 130,
    artworkFile: 'qrbg.png',
    window: { x: 346, y: 295, width: 842, height: 1240 },
    bottomTextMask: { x: 32, y: 1260, width: 1471, height: 200 },
    // QR 755 px at (390, 338): the view design's 43 px top margin, 43 px sides.
    // Bottom row: logo box (footer height) at the left, steps footer centred.
    ordersProfile: {
      widthMm: 130,
      heightMm: 130,
      padding: { top: 28.6, right: 6, bottom: 6, left: 6 },
      offsetXMm: 0,
      offsetYMm: 0,
      qrScale: 1,
      titleFontSizeMm: 8,
      titleGapMm: 2.5,
      logoSizeMm: 15, // unused while logoMatchesFooter is on
      logoOffsetMm: 4,
      logoMatchesFooter: true,
      footerWidthMm: 50,
      footerOffsetBottomMm: 0, // lengths are at least 1 px, so the row ends 1 px above the content
    },
  },
  vertical: {
    widthPx: 1181,
    heightPx: 1772,
    widthMm: 100,
    heightMm: 150,
    artworkFile: 'qrbg1015.png',
    window: { x: 169, y: 295, width: 842, height: 1477 },
    bottomTextMask: { x: 28, y: 1355, width: 1125, height: 165 },
    // QR 756 px at (213, 338): the view design's 43 px top margin, 43 px sides.
    // The narrower poster gets a smaller steps footer so the logo box (same
    // height, near the left edge) fits beside the centred footer.
    ordersProfile: {
      widthMm: 100,
      heightMm: 150,
      padding: { top: 28.6, right: 6, bottom: 25.2, left: 6 },
      offsetXMm: 0,
      offsetYMm: 0,
      qrScale: 1,
      titleFontSizeMm: 9,
      titleGapMm: 3.5,
      logoSizeMm: 11, // unused while logoMatchesFooter is on
      logoOffsetMm: 1.5,
      logoMatchesFooter: true,
      footerWidthMm: 44,
      footerOffsetBottomMm: 0, // lengths are at least 1 px, so the row ends 1 px above the content
    },
  },
};

export function getMenuQrPosterPxPerMm(size: TMenuQrPosterSize): number {
  const spec = MENU_QR_POSTERS[size];

  return spec.widthPx / spec.widthMm;
}

/** Title centered at `centerX`, anchored at its baseline (canvas textBaseline 'alphabetic'). */
export interface IMenuQrViewPosterLayout {
  qr: IMenuQrRect;
  title: {
    centerX: number;
    baselineY: number;
    fontPx: number;
    maxWidth: number;
  };
}

/** The legacy "menu only" poster geometry (QR at 50 % / 65 % of the width). */
export function computeViewPosterLayout(
  size: TMenuQrPosterSize
): IMenuQrViewPosterLayout {
  const { widthPx, heightPx } = MENU_QR_POSTERS[size];
  const isSquare = size === 'square';
  const qrWidth = widthPx * (isSquare ? 0.5 : 0.65);
  const qrTop = isSquare
    ? heightPx / 2 - qrWidth / 2 - qrWidth * 0.06
    : heightPx / 2 - qrWidth * 0.72;
  const fontPx = ((isSquare ? 36 : 48) / 500) * widthPx;
  const sideMargin = isSquare ? 60 : 40;

  return {
    qr: {
      x: widthPx / 2 - qrWidth / 2,
      y: qrTop,
      width: qrWidth,
      height: qrWidth,
    },
    title: {
      centerX: widthPx / 2,
      baselineY: qrTop + qrWidth + (isSquare ? fontPx : fontPx * 1.5),
      fontPx,
      maxWidth: widthPx - sideMargin * 2,
    },
  };
}
